// src/components/TaskCalendar/TaskCalendarModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import { X } from 'lucide-react';
import { assignedTasksApi, AssignedTask } from '../../api/assignedTasks';
import { meetingsApi, Meeting } from '../../api/meetings';
import MeetingModal from '../MeetingModal';
import { useAuth } from '../../contexts/AuthContext';
import TaskModal from './TaskModal';

interface TaskCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

const ACTIVE_STATUSES: AssignedTask['status'][] = [
  'new',
  'in_progress',
  'pending_review',
  'revision',
];

const TaskCalendarModal: React.FC<TaskCalendarModalProps> = ({
  isOpen,
  onClose,
  anchorEl,
}) => {
  const { user } = useAuth();
  const today = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();
  const [selectedDate, setSelectedDate] = useState<string | null>(today);
  const [tasks, setTasks] = useState<AssignedTask[]>([]); // все задачи, для точек
  const [reviewDates, setReviewDates] = useState<Set<string>>(new Set()); // даты с pending_review (для создателя)
  const [meetings, setMeetings] = useState<Meeting[]>([]); // встречи
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [activeTab, setActiveTab] = useState<'meetings' | 'tasks'>('meetings');
  const [showTaskModalForDate, setShowTaskModalForDate] = useState<string | null>(null);
  const [popoverHidden, setPopoverHidden] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const MARGIN = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const POPOVER_W = Math.min(720, vw - MARGIN * 2);
    const POPOVER_H = Math.min(620, vh - MARGIN * 2);

    // Позиция ПОД кнопкой (fixed → координаты viewport)
    let left = rect.left;
    let top = rect.bottom + 8;

    if (left + POPOVER_W > vw - MARGIN) left = vw - POPOVER_W - MARGIN;
    if (left < MARGIN) left = MARGIN;

    if (top + POPOVER_H > vh - MARGIN) {
      const aboveTop = rect.top - POPOVER_H - 8;
      top = aboveTop < MARGIN ? MARGIN : aboveTop;
    }

    setPosition({ top, left });
  };

  useEffect(() => {
    if (isOpen && anchorEl) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, anchorEl]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Не закрываем, если открыта модалка задачи или встречи
      if ((target as Element).closest?.('.task-modal-container')) return;
      if ((target as Element).closest?.('.meeting-modal-container')) return;
      // Проверяем также по data-атрибутам (более надёжно)
      if ((target as Element).closest?.('[data-meeting-modal]')) return;

      // Закрытие — только по кнопке X
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorEl]);

  const fetchAllTasks = async () => {
    if (!user) return;
    try {
      // Задачи, назначенные пользователю (активные)
      const assignedRes = await assignedTasksApi.getTasks({ assigneeId: user.id });
      const activeAssigned = assignedRes.data.filter((task: AssignedTask) =>
        ACTIVE_STATUSES.includes(task.status)
      );

      // Задачи, где пользователь создатель и статус pending_review
      const createdRes = await assignedTasksApi.getTasks({ creatorId: user.id });
      const pendingReview: AssignedTask[] = createdRes.data.filter(
        (task: AssignedTask) => task.status === 'pending_review'
      );

      // Объединяем для точек
      const allTasks = [...activeAssigned, ...pendingReview];
      setTasks(allTasks);

      // Собираем даты с pending_review
      const reviewSet = new Set<string>();
      pendingReview.forEach((task: AssignedTask) => {
        if (task.dueDate) reviewSet.add(task.dueDate);
      });
      setReviewDates(reviewSet);
    } catch (err) {
      console.error('Ошибка загрузки задач:', err);
    }
  };

  const fetchMeetings = async () => {
    try {
      // Грузим ±30 дней от текущего месяца
      const from = new Date();
      from.setMonth(from.getMonth() - 1);
      const to = new Date();
      to.setMonth(to.getMonth() + 2);
      const list = await meetingsApi.list({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      setMeetings((list || []).filter(m => m.status !== 'cancelled' && m.status !== 'completed'));
    } catch (err) {
      console.error('Ошибка загрузки встреч:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAllTasks();
      fetchMeetings();
    }
  }, [isOpen, user]);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    const dateStr = formatDate(date);
    const hasTasks = tasks.some((task) => task.dueDate === dateStr);
    const hasMeetings = meetings.some((m) => {
      const ms = new Date(m.startsAt);
      return formatDate(ms) === dateStr;
    });
    if (!hasTasks && !hasMeetings) return null;
    const isReview = reviewDates.has(dateStr);
    return (
      <div style={{ display: 'flex', gap: 2, marginTop: 2, justifyContent: 'center' }}>
        {hasTasks && (
          <div className={`task-calendar-dot ${isReview ? 'dot-review' : ''}`} />
        )}
        {hasMeetings && <div className="task-calendar-dot dot-meeting" />}
      </div>
    );
  };

  const handleDateChange = (value: any) => {
    if (!value) return;
    const date = Array.isArray(value) ? value[0] : value;
    if (!date) return;
    setSelectedDate(formatDate(date));
  };

  

  return (
    <>
      {isOpen && !popoverHidden && (
      <div
        ref={popoverRef}
        style={{
          ...styles.popover,
          top: position.top,
          left: position.left,
          width: Math.min(720, (typeof window !== 'undefined' ? window.innerWidth : 720) - 32),
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'min(85vh, 640px)',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {/* Заголовок */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            📅 Календарь
          </h3>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 6, borderRadius: 8, color: '#64748b',
            display: 'flex', alignItems: 'center',
          }}>
            <X size={18} />
          </button>
        </div>

        {/* 2 колонки */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* ─── Левая колонка: Календарь ─── */}
          <div style={{
            width: 360, minWidth: 320, flexShrink: 0,
            padding: '14px 18px',
            borderRight: '1px solid rgba(0,0,0,0.06)',
            overflowY: 'auto',
          }}>
            <Calendar
              onChange={handleDateChange}
              value={selectedDate ? new Date(selectedDate) : null}
              tileContent={tileContent}
              locale="ru-RU"
              className="task-calendar"
            />

            {/* Легенда */}
            <div style={styles.legend}>
              <div style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: '#ff69b4' }} /> Задачи
              </div>
              <div style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: '#8b5cf6', boxShadow: '0 0 6px #8b5cf6' }} /> Встречи
              </div>
            </div>
          </div>

          {/* ─── Правая колонка: содержимое дня ─── */}
          <div style={{
            flex: 1, minWidth: 0,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Дата */}
            <div style={{
              padding: '14px 18px 10px',
              borderBottom: '1px solid rgba(0,0,0,0.04)',
            }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#0f172a',
                textTransform: 'capitalize',
              }}>
                {selectedDate
                  ? new Date(selectedDate).toLocaleDateString('ru-RU', {
                      weekday: 'long', day: 'numeric', month: 'long',
                    })
                  : 'Выберите дату'}
              </div>
            </div>

            {/* Переключатель Встречи / Задачи */}
            <div style={{
              display: 'flex', gap: 6,
              padding: '10px 18px',
              borderBottom: '1px solid rgba(0,0,0,0.04)',
            }}>
              <button
                onClick={() => setActiveTab('meetings')}
                style={{
                  flex: 1,
                  background: activeTab === 'meetings'
                    ? 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
                    : 'rgba(139, 92, 246, 0.08)',
                  color: activeTab === 'meetings' ? '#fff' : '#6d28d9',
                  border: 'none', borderRadius: 10,
                  padding: '8px 12px',
                  fontSize: 12, fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                🎥 Встречи ({
                  selectedDate
                    ? meetings.filter(m => formatDate(new Date(m.startsAt)) === selectedDate).length
                    : 0
                })
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                style={{
                  flex: 1,
                  background: activeTab === 'tasks'
                    ? 'linear-gradient(135deg, #ff69b4, #ec4899)'
                    : 'rgba(255, 105, 180, 0.08)',
                  color: activeTab === 'tasks' ? '#fff' : '#be185d',
                  border: 'none', borderRadius: 10,
                  padding: '8px 12px',
                  fontSize: 12, fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                📋 Задачи ({
                  selectedDate
                    ? tasks.filter(t => t.dueDate === selectedDate).length
                    : 0
                })
              </button>
            </div>

            {/* Список */}
            <div style={{
              flex: 1, overflowY: 'auto',
              padding: '12px 18px',
            }}>
              {!selectedDate && (
                <div style={styles.empty}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
                  <div>Выберите дату в календаре</div>
                </div>
              )}

              {selectedDate && activeTab === 'meetings' && (() => {
                const dayMeetings = meetings
                  .filter(m => formatDate(new Date(m.startsAt)) === selectedDate)
                  .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

                if (dayMeetings.length === 0) {
                  return (
                    <div style={styles.empty}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>🎥</div>
                      <div>Нет встреч на этот день</div>
                    </div>
                  );
                }

                return (
                  <div style={styles.sectionList}>
                    {dayMeetings.map(m => {
                      const start = new Date(m.startsAt);
                      const end = new Date(m.endsAt);
                      const isCancelled = m.status === 'cancelled';
                      return (
                        <button
                          key={m._id}
                          onClick={() => { setSelectedMeeting(m); setPopoverHidden(true); }}
                          style={{
                            ...styles.meetingCard,
                            opacity: isCancelled ? 0.55 : 1,
                          }}
                        >
                          <div style={styles.meetingTime}>
                            {start.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                            {' – '}
                            {end.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div style={styles.meetingBody}>
                            <div style={{
                              ...styles.meetingTitle,
                              textDecoration: isCancelled ? 'line-through' : 'none',
                            }}>
                              {m.title}
                            </div>
                            {m.candidate && (
                              <div style={styles.meetingSub}>
                                👤 {m.candidate.lastName} {m.candidate.firstName}
                              </div>
                            )}
                            {m.location && (
                              <div style={styles.meetingSub}>📍 {m.location}</div>
                            )}
                          </div>
                          {isCancelled && <div style={styles.meetingBadgeCancel}>Отменена</div>}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {selectedDate && activeTab === 'tasks' && (() => {
                const dayTasks = tasks.filter(t => t.dueDate === selectedDate);
                return (
                  <>
                    <button
                      onClick={() => { setShowTaskModalForDate(selectedDate); setPopoverHidden(true); }}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #ff69b4, #ec4899)',
                        color: '#fff', border: 'none',
                        borderRadius: 12, padding: '10px 14px',
                        fontSize: 13, fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        marginBottom: 12,
                        boxShadow: '0 4px 12px rgba(236,72,153,0.25)',
                      }}
                    >
                      ➕ Создать задачу на {new Date(selectedDate).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
                    </button>

                    {dayTasks.length === 0 ? (
                      <div style={styles.empty}>
                        <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                        <div>Нет задач на этот день</div>
                      </div>
                    ) : (
                      <div style={styles.sectionList}>
                        {dayTasks.map(t => (
                          <button
                            key={(t as any).id || (t as any)._id}
                            onClick={() => { setShowTaskModalForDate(selectedDate); setPopoverHidden(true); }}
                            style={{
                              ...styles.taskCard,
                              cursor: 'pointer',
                              width: '100%',
                              textAlign: 'left',
                              fontFamily: 'inherit',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(255,105,180,0.2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                          >
                            <div style={{
                              ...styles.taskDot,
                              background: reviewDates.has(selectedDate) ? '#f0ad4e' : '#ff69b4',
                            }} />
                            <div style={styles.taskBody}>
                              <div style={styles.taskTitle}>{t.title}</div>
                              {t.description && (
                                <div style={styles.taskSub} title={t.description}>
                                  {t.description.length > 80 ? t.description.slice(0, 80) + '…' : t.description}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* TaskModal — задачи на выбранную дату */}
      {showTaskModalForDate && (
        <TaskModal
          date={showTaskModalForDate}
          isOpen={!!showTaskModalForDate}
          onClose={() => { setShowTaskModalForDate(null); setPopoverHidden(false); }}
          onTaskUpdate={() => { fetchAllTasks(); setShowTaskModalForDate(null); setPopoverHidden(false); }}
        />
      )}

      {/* MeetingModal для редактирования встречи из календаря */}
      {selectedMeeting && (
        <MeetingModal
          candidateId={selectedMeeting.candidateId}
          candidateName={selectedMeeting.candidate
            ? `${selectedMeeting.candidate.lastName || ''} ${selectedMeeting.candidate.firstName || ''}`.trim()
            : ''}
          meeting={selectedMeeting}
          mode="view"
          onClose={() => { setSelectedMeeting(null); setPopoverHidden(false); }}
          onSaved={() => { setSelectedMeeting(null); setPopoverHidden(false); fetchMeetings(); }}
          onEdit={(m) => { setSelectedMeeting(null); /* клик «Редактировать» открывает edit-режим через MeetingModal */ }}
        />
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  popover: {
    position: 'fixed',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    zIndex: 1100,
    width: '400px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: '#1a1a2e',
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
  },
  calendarWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  legend: {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    padding: '8px 0 4px',
    fontSize: 11,
    color: '#64748b',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  contentArea: {
    marginTop: 10,
    borderTop: '1px solid rgba(0,0,0,0.06)',
    paddingTop: 12,
    maxHeight: 340,
    overflowY: 'auto',
  },
  contentHeader: {
    fontSize: 13,
    fontWeight: 700,
    color: '#0f172a',
    textTransform: 'capitalize',
    marginBottom: 10,
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  sectionIconMeeting: { fontSize: 14 },
  sectionIconTask: { fontSize: 14 },
  sectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  meetingCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(139,92,246,0.04))',
    border: '1px solid rgba(139,92,246,0.2)',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
  },
  meetingTime: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6d28d9',
    minWidth: 80,
    flexShrink: 0,
  },
  meetingBody: { flex: 1, minWidth: 0 },
  meetingTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: 2,
  },
  meetingSub: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 1,
  },
  meetingBadgeCancel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#ef4444',
    background: '#fee2e2',
    padding: '2px 6px',
    borderRadius: 6,
    flexShrink: 0,
  },
  taskCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    background: 'rgba(255, 105, 180, 0.04)',
    border: '1px solid rgba(255, 105, 180, 0.15)',
    borderRadius: 10,
    padding: '8px 12px',
  },
  taskDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 6,
    flexShrink: 0,
  },
  taskBody: { flex: 1, minWidth: 0 },
  taskTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: '#0f172a',
  },
  taskSub: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 2,
  },
  empty: {
    textAlign: 'center',
    padding: '24px 0',
    color: '#94a3b8',
    fontSize: 13,
  },

};

// Глобальные стили календаря
const calendarStyles = `
  .task-calendar {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    width: 100%;
    font-family: inherit;
  }

  .task-calendar .react-calendar__navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(8px);
    border-radius: 60px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .task-calendar .react-calendar__navigation button {
    background: transparent;
    border: none;
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
    cursor: pointer;
    padding: 6px 14px;
    border-radius: 40px;
    transition: all 0.2s;
  }

  .task-calendar .react-calendar__navigation button:enabled:hover,
  .task-calendar .react-calendar__navigation button:enabled:focus {
    background: linear-gradient(135deg, rgba(106, 17, 203, 0.8), rgba(37, 117, 252, 0.8));
    color: white;
    box-shadow: 0 4px 12px rgba(37, 117, 252, 0.3);
    transform: scale(1.02);
  }

  .task-calendar .react-calendar__navigation button[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .task-calendar .react-calendar__month-view__weekdays {
    text-transform: uppercase;
    font-weight: 600;
    font-size: 11px;
    color: #888;
    text-align: center;
    padding: 8px 0;
    background: transparent;
  }

  .task-calendar .react-calendar__month-view__weekdays__weekday {
    padding: 4px;
  }

  .task-calendar .react-calendar__month-view__weekdays abbr {
    text-decoration: none;
    cursor: default;
  }

  .task-calendar .react-calendar__tile {
    background: transparent !important;
    border-radius: 50% !important;
    box-shadow: none !important;
    transition: all 0.2s ease;
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 14px;
    color: #1a1a2e;
    position: relative;
    cursor: pointer;
    border: none !important;
    outline: none !important;
  }

  .task-calendar .react-calendar__tile:enabled:hover,
  .task-calendar .react-calendar__tile:enabled:focus {
    background: rgba(37, 117, 252, 0.15) !important;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .task-calendar .react-calendar__tile--active {
    background: linear-gradient(135deg, #6a11cb, #2575fc) !important;
    color: white !important;
    box-shadow: 0 6px 14px rgba(37, 117, 252, 0.4);
  }

  .task-calendar .react-calendar__tile--now {
    background: rgba(255, 105, 180, 0.25) !important;
    color: #1a1a2e;
    font-weight: 600;
    backdrop-filter: blur(2px);
  }

  .task-calendar .react-calendar__tile--now:enabled:hover,
  .task-calendar .react-calendar__tile--now:enabled:focus {
    background: rgba(255, 105, 180, 0.5) !important;
  }

  .task-calendar .react-calendar__month-view__days__day--weekend {
    color: #ff4d4f;
  }

  .task-calendar .react-calendar__month-view__days__day--neighboringMonth {
    color: #aaa;
  }

  /* Обычная точка (розовая) */
  .task-calendar-dot {
    width: 6px;
    height: 6px;
    background: #ff69b4;
    border-radius: 3px;
    margin-top: 4px;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.6);
  }

  /* Жёлтая точка для задач на согласование */
  .task-calendar-dot.dot-review {
    background: #f0ad4e;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.6);
  }

  /* Фиолетовая точка для встреч */
  .task-calendar-dot.dot-meeting {
    background: #8b5cf6;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.6), 0 0 6px rgba(139, 92, 246, 0.7);
  }

  .task-calendar .react-calendar__tile--active .task-calendar-dot {
    background: white;
    box-shadow: none;
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('task-calendar-styles')) {
  const styleTag = document.createElement('style');
  styleTag.id = 'task-calendar-styles';
  styleTag.textContent = calendarStyles;
  document.head.appendChild(styleTag);
}

export default TaskCalendarModal;